import {
    FormEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useState
} from 'react';
import {
    UIButton,
    UIFormInput,
    UIInlineAlert,
    UISpacer,
    UIText,
    UITextButton
} from '@/ui';
import { useRequestSignupVerificationAction } from '../actions';
import { useMeTranslation } from '../hooks';
import styles from './request-signup-verification.module.scss';
import { Link } from 'react-router';
import { validateEmail } from '@/common/util/validators/email';

type RequestSignupVerificationProps = {
    defaultEmail?: string;
    onSuccess?: (data: { email: string }) => void;
};

export function RequestSignupVerification(
    props: RequestSignupVerificationProps
): ReactElement {
    const { defaultEmail, onSuccess } = props;

    const { t } = useMeTranslation();

    const { requestSignupVerification, loading, error, clearError } =
        useRequestSignupVerificationAction();

    return (
        <>
            <UIText variant="h2" bold>
                {t('components.request_signup_verification.title')}
            </UIText>
            <UISpacer multiplier={2} />
            <UIText>
                {t('components.request_signup_verification.description')}
            </UIText>
            <UISpacer multiplier={2} />
            {error && (
                <>
                    <UIInlineAlert text={error} onClose={clearError} />
                    <UISpacer multiplier={2} />
                </>
            )}
            <RequestSignupVerificationForm
                loading={loading}
                defaultValues={{ email: defaultEmail }}
                onSubmit={(data) => {
                    requestSignupVerification({ email: data.email })
                        .then(() => {
                            onSuccess?.(data);
                        })
                        .catch(() => {}); // Handled in the hook
                }}
            />
        </>
    );
}

type RequestSignupVerificationFormProps = {
    onSubmit?: (data: { email: string }) => void;
    loading?: boolean;
    defaultValues?: { email?: string };
};
export function RequestSignupVerificationForm(
    props: RequestSignupVerificationFormProps
): ReactElement {
    const { t } = useMeTranslation();

    const { onSubmit, loading, defaultValues } = props;

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Email
     */
    const [email, setEmail] = useState(defaultValues?.email || '');
    const [emailError, setEmailError] = useState('');
    const checkEmail = useCallback(
        (email: string): string => {
            const error = validateEmail(email);
            if (error) {
                return t(error);
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setEmailError(checkEmail(email));
        }
    }, [email, showRealTimeErrors, checkEmail]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkEmail(email)) {
            setShowRealTimeErrors(false);
            onSubmit?.({ email });
        }
    };

    return (
        <div className={styles.container}>
            <form onSubmit={handleSubmit} noValidate>
                <UIFormInput
                    name="email"
                    label={t('shared.fields.email.label')}
                    value={email}
                    autoFocus={true}
                    type="email"
                    errorText={emailError}
                    required
                    maxLength={320}
                    onChange={setEmail}
                />
                <UISpacer multiplier={2} />
                <div className={styles.buttonContainer}>
                    <UIButton
                        variant="primary"
                        type="submit"
                        loading={loading}
                        disabled={loading}
                    >
                        {t('shared.labels.sign_up')}
                    </UIButton>
                    <UISpacer multiplier={1} />
                    <Link to={'/login'} style={{ textDecoration: 'none' }}>
                        <UITextButton variant="primary">
                            {t('shared.labels.return_to_login')}
                        </UITextButton>
                    </Link>
                </div>
            </form>
        </div>
    );
}
